/**
 * Class MegaMenu
 */
define([
    'jquery',
    'isSmallDevice',
    'toggleBodyScroll'
], ($, isSmallDevice, toggleBodyScroll) => {
    class MegaMenu {

        /**
         * Constructor
         *
         * @param {Object}  config  Configuration options passed in through JSON
         * @param {Element} element The element that this class was bound to
         *
         * @returns {null}
         */
        constructor(config, element) {
            const defaultConfig = {
                navMenuContainerSelector: '.navigation',
                navOverlaySelector: '.nav-open-overlay',
                navMenuListSelector: '.nav-menu-list',
                navLinksSelector: 'a.level-top',
                submenuStickyHeaderSelector: '.submenu-sticky-header',
                submenuContentContainerSelector: 'div.submenu',
                linkWithSubmenuSelector: '.nav--with-submenu',
                activatedLinkWithSubmenuSelector: '.nav--with-submenu.active',

                navMenuIconActiveClass: 'is-active',
                navMenuActiveClass: 'nav-open',
                linkWithSubmenuClass: 'nav--with-submenu',
                linkActiveClass: 'active',
                submenuActiveClass: 'submenu-active',

                tabletBreakpoint: 1024
            };
            // Merge default config options and provided config
            /** @type {Object} */
            this.config = { ...defaultConfig, ...config };
            /** @type {jQuery} */
            this.$body = $('body');

            this.$navMenuIcon = $(element);
            this.$navMenuContainer = $(this.config.navMenuContainerSelector);
            this.$navMenuOverlay = $(this.config.navOverlaySelector);
            this.$navMenu = $(this.config.navMenuListSelector);
            this.$navLinks = this.$navMenuContainer.find(this.config.navLinksSelector);
            this.$navSubmenuStickyHeaders = this.$navMenuContainer.find(this.config.submenuStickyHeaderSelector);

            this.attachListeners();
            this.processSubCategoryLinks();
        }

        /**
         * Attach event listeners to elements
         *
         * @returns {null}
         */
        attachListeners() {
            /**
             * Nav menu overlay click event handler
             *
             * @returns {null}
             */
            this.$navMenuOverlay.on('click', () => {
                this.handleNavMenuOverlayClickEvent();
            });

            /**
             * Show submenu on click if its small device and if its has submenu
             *
             * @returns {null}
             */
            this.$navLinks.on('click', (e) => {
                this.handleNavLinkClickEvent(e);
            });

            /**
             * Remove the active class from activated nav item link and menu
             */
            this.$navSubmenuStickyHeaders.on('click', (e) => {
                this.handleSubmenuStickyHeaderClickEvent(e);
            });

            /**
             * Show menu in mobile and toggle nav-open class to body, and reset the menu
             *
             * @returns {null}
             */
            this.$navMenuIcon.on('click', () => {
                this.handleNavMenuIconClickEvent();
            });
        }

        /**
         * Handle click event of nav menu overlay, it's supposed to reset the menu,
         * close (collapse) the activated (expanded) menu, toggle cross menu icon to
         * hamburger menu icon, show scroll bars of body
         *
         * @returns {null}
         */
        handleNavMenuOverlayClickEvent() {
            this.closeActivatedSubmenu();
            this.toggleMenu(false);
            this.toggleMenuIcon(false);
            this.toggleBodyScrollBars(true);
        }

        /**
         * Handle click event of nav menu overlay, it's supposed to reset the menu,
         * close (collapse) the activated (expanded) menu, toggle cross menu icon to
         * hamburger menu icon, show scroll bars of body
         *
         * @returns {null}
         */
        processSubCategoryLinks() {
            const container = document.querySelectorAll('.category-description .pb-buttonlist__items')[0];
            const items = document.querySelectorAll('.category-description .pb-buttonlist__item');

            if (container && items.length > 0) {
                let itemLength = 0;

                for (let i = 0, len = items.length; i < len; i++) {
                    itemLength = itemLength + items[i]?.offsetWidth;
                }
                if (container.offsetWidth > itemLength) {
                    container.style.justifyContent = 'center';
                } else {
                    container.style.justifyContent = 'left';
                }

                this.initSubCategoryDrag();
            }
        }

        initSubCategoryDrag () {
            const scrollContainer = document.getElementsByClassName('pb-buttonlist__items')[0];
            let isDragging = false;
            let startX, scrollLeft;

            scrollContainer.addEventListener('mousedown', (event) => {
                isDragging = event.target.classList.contains('pb-buttonlist__items');
                scrollContainer.style.cursor = 'grabbing';
                startX = event.pageX - scrollContainer.offsetLeft;
                scrollLeft = scrollContainer.scrollLeft;
            });

            scrollContainer.addEventListener('mouseleave', () => {
                isDragging = false;
                scrollContainer.style.cursor = 'grab';
            });

            scrollContainer.addEventListener('mouseup', () => {
                isDragging = false;
                scrollContainer.style.cursor = 'grab';
            });

            scrollContainer.addEventListener('mousemove', (event) => {
                if (!isDragging) return;
                event.preventDefault();
                const x = event.pageX - scrollContainer.offsetLeft;
                const walk = (x - startX) * 1.2;
                scrollContainer.scrollLeft = scrollLeft - walk;
            });
        }

        /**
         * Handle click event of nav link item
         *
         * @param {Object} e click event object
         * @returns {null}
         */
        handleNavLinkClickEvent(e) {
            const $clickedLink = $(e.currentTarget);
            const $clickedLinkContainer = $clickedLink.parent();

            if (!isSmallDevice(this.config.tabletBreakpoint) || !$clickedLinkContainer.hasClass(this.config.linkWithSubmenuClass)) {
                return null;
            }

            e.preventDefault();

            if (window.innerWidth === this.config.tabletBreakpoint && $clickedLinkContainer.hasClass(this.config.linkActiveClass)) {
                this.resetMenuForTablet();
                return null;
            }

            this.resetMenuForTablet();
            this.resetSubmenuScrollBarPosition($clickedLinkContainer);
            this.toggleSubmenuOfClickedLink($clickedLinkContainer);

            return null;
        }

        /**
         * Remove active class for tablet screen (1024px) to collapse expanded menu
         *
         * @returns {null}
         */
        resetMenuForTablet() {
            if (window.innerWidth !== this.config.tabletBreakpoint) {
                return null;
            }

            this.$navMenu.children(this.config.linkWithSubmenuSelector).removeClass(this.config.linkActiveClass);
            this.$navMenu.removeClass(this.config.submenuActiveClass);

            return null;
        }

        /**
         * Reset scroll bar of submenu when it's closed (collapsed) so that next time when the user expand
         * the same or other submenu, they can see the top content of submenu
         *
         * @param {Object} $clickedLinkContainer clicked nav link item that contains sticky header and submenu
         * @returns {null}
         */
        resetSubmenuScrollBarPosition($clickedLinkContainer) {
            $clickedLinkContainer.find(this.config.submenuContentContainerSelector).scrollTop(0);
        }

        /**
         *  Toggle active class on menu and container of clicked link to expand/collapse
         *  submenu of clicked link
         *
         * @param {Object} $clickedLinkContainer container element of clicked nav link
         * returns {null}
         */
        toggleSubmenuOfClickedLink($clickedLinkContainer) {
            const isLinkContainerActivated = $clickedLinkContainer.hasClass(this.config.linkActiveClass);

            $clickedLinkContainer.toggleClass(this.config.linkActiveClass, !isLinkContainerActivated);
            this.$navMenu.toggleClass(this.config.submenuActiveClass, !isLinkContainerActivated);
        }

        /**
         * Handle click event of element that initials mega menu JS module,
         * usually element is the hamburger menu icon, hide scroll bar of body
         * if menu is activated (expanded)
         *
         * @returns {null}
         */
        handleNavMenuIconClickEvent() {
            const isMenuActivated = this.$body.hasClass(this.config.navMenuActiveClass);

            this.closeActivatedSubmenu();
            this.toggleMenu(!isMenuActivated);
            this.toggleMenuIcon(!isMenuActivated);
            this.toggleBodyScrollBars(isMenuActivated);
        }

        /**
         * Handle click event of sticky header in submenu to close the submenu
         *
         * @param {Object} e click event object
         * @returns {null}
         */
        handleSubmenuStickyHeaderClickEvent(e) {
            e.preventDefault();
            this.closeActivatedSubmenu();
        }

        /**
         * Remove the active class from nav menu and clicked nav link to close (collapse) the
         * activated (expanded) submenu
         *
         * @returns {null}
         */
        closeActivatedSubmenu() {
            const $activatedLink = $(this.config.activatedLinkWithSubmenuSelector);

            $activatedLink.removeClass(this.config.linkActiveClass);
            this.$navMenu.removeClass(this.config.submenuActiveClass);
        }

        /**
         * Toggle the menu display by adding/removing the active classes from <body>
         *
         * @params {Boolean} toggle add(true) or remove(false) the class
         * @returns {null}
         */
        toggleMenu(toggle) {
            this.$body.toggleClass(this.config.navMenuActiveClass, toggle);
        }

        /**
         * Toggle the menu icon by adding/removing the active classes from menu icon element
         * it will display different icons based on the activated status
         *
         * @params {Boolean} toggle add(true) or remove(false) the class
         * @returns {null}
         */
        toggleMenuIcon(toggle) {
            this.$navMenuIcon.toggleClass(this.config.navMenuIconActiveClass, toggle);
        }

        /**
         * Toggle the scroll bars of body based on the activated status of nav menu,
         * if the nav menu is activated (expanded), then no scroll bars should be applied,
         * if the nav menu is closed (collapsed), then scroll bars should be seen
         *
         * @params {Boolean} toggle add(true) or remove(false) the class
         * @returns {null}
         */
        toggleBodyScrollBars(toggle) {
            toggleBodyScroll(toggle);
        }

    }

    return (config, element) => {
        return new MegaMenu(config, element);
    };
});
